/**
 * G-7 — tenant rollout state.
 *
 * Weekly UAT containment and named-provider packs live in the typed
 * tenant_rollout_states control plane. tenants.brand_config.rolloutState is
 * retained as an atomic mixed-version compatibility projection only.
 *
 * Plan: payroll-programme-implementation-plan-2026-08-15.md:602-609.
 */

import { DISPATCH_RELIABILITY_FLAG_DB_KEYS } from './dispatch-reliability-flags';

export const TENANT_ROLLOUT_STATE_VERSION = 1 as const;

export const TENANT_ROLLOUT_SOURCE_OPERATORS = ['NONE', 'AMANDA_R331'] as const;
export const TENANT_ROLLOUT_ROSTER_PACKS = ['NONE', 'MEDEA_V1'] as const;

export type TenantRolloutSourceOperator = (typeof TENANT_ROLLOUT_SOURCE_OPERATORS)[number];
export type TenantRolloutRosterPack = (typeof TENANT_ROLLOUT_ROSTER_PACKS)[number];

export type TenantRolloutState = {
  version: typeof TENANT_ROLLOUT_STATE_VERSION;
  containedUntilUat: boolean;
  releasedDispatchReliabilityFlagKeys: string[];
  sourceOperator: TenantRolloutSourceOperator;
  rosterOperationalPack: TenantRolloutRosterPack;
};

export type TenantRolloutStatePatch = Partial<Omit<TenantRolloutState, 'version'>>;

export type TenantRolloutStateSource = 'typed' | 'brand_config' | 'implied';

export type TenantRolloutStateSnapshot = {
  state: TenantRolloutState;
  source: TenantRolloutStateSource;
  rowVersion: number;
  updatedAt: string | null;
};

const SOURCE_OPERATORS = new Set<string>(TENANT_ROLLOUT_SOURCE_OPERATORS);
const ROSTER_PACKS = new Set<string>(TENANT_ROLLOUT_ROSTER_PACKS);
const RELEASED_DISPATCH_KEYS = new Set<string>(Object.values(DISPATCH_RELIABILITY_FLAG_DB_KEYS));
const PATCH_KEYS = new Set([
  'containedUntilUat',
  'releasedDispatchReliabilityFlagKeys',
  'sourceOperator',
  'rosterOperationalPack',
]);

export function defaultTenantRolloutState(): TenantRolloutState {
  return {
    version: TENANT_ROLLOUT_STATE_VERSION,
    containedUntilUat: false,
    releasedDispatchReliabilityFlagKeys: [],
    sourceOperator: 'NONE',
    rosterOperationalPack: 'NONE',
  };
}

export function parseTenantRolloutState(value: unknown): TenantRolloutState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record.version !== TENANT_ROLLOUT_STATE_VERSION) return null;
  if (typeof record.containedUntilUat !== 'boolean') return null;
  if (typeof record.sourceOperator !== 'string' || !SOURCE_OPERATORS.has(record.sourceOperator)) {
    return null;
  }
  if (
    typeof record.rosterOperationalPack !== 'string' ||
    !ROSTER_PACKS.has(record.rosterOperationalPack)
  ) {
    return null;
  }
  if (!Array.isArray(record.releasedDispatchReliabilityFlagKeys)) return null;
  const releasedDispatchReliabilityFlagKeys = record.releasedDispatchReliabilityFlagKeys;
  if (
    releasedDispatchReliabilityFlagKeys.some(
      (key) => typeof key !== 'string' || key.trim() !== key || !RELEASED_DISPATCH_KEYS.has(key),
    ) ||
    new Set(releasedDispatchReliabilityFlagKeys).size !== releasedDispatchReliabilityFlagKeys.length
  ) {
    return null;
  }
  return {
    version: TENANT_ROLLOUT_STATE_VERSION,
    containedUntilUat: record.containedUntilUat,
    releasedDispatchReliabilityFlagKeys: [...releasedDispatchReliabilityFlagKeys].sort(),
    sourceOperator: record.sourceOperator as TenantRolloutSourceOperator,
    rosterOperationalPack: record.rosterOperationalPack as TenantRolloutRosterPack,
  };
}

function parseLegacyTenantRolloutState(value: unknown): TenantRolloutState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record.version !== TENANT_ROLLOUT_STATE_VERSION) return null;
  if (typeof record.containedUntilUat !== 'boolean') return null;
  if (typeof record.sourceOperator !== 'string' || !SOURCE_OPERATORS.has(record.sourceOperator)) {
    return null;
  }
  if (
    typeof record.rosterOperationalPack !== 'string' ||
    !ROSTER_PACKS.has(record.rosterOperationalPack)
  ) {
    return null;
  }
  if (!Array.isArray(record.releasedDispatchReliabilityFlagKeys)) return null;

  // Match the established v1 reader's effective behavior: malformed/unknown
  // entries never released a registered containment key, but they also never
  // disabled a valid Amanda/Medea selection. Keep known effective releases and
  // normalise only the inert array noise during mixed-version recovery.
  const releasedDispatchReliabilityFlagKeys = [
    ...new Set(
      record.releasedDispatchReliabilityFlagKeys.filter(
        (key): key is string => typeof key === 'string' && RELEASED_DISPATCH_KEYS.has(key),
      ),
    ),
  ].sort();
  return {
    version: TENANT_ROLLOUT_STATE_VERSION,
    containedUntilUat: record.containedUntilUat,
    releasedDispatchReliabilityFlagKeys,
    sourceOperator: record.sourceOperator as TenantRolloutSourceOperator,
    rosterOperationalPack: record.rosterOperationalPack as TenantRolloutRosterPack,
  };
}

export function readTenantRolloutState(brandConfig: Record<string, unknown> | null | undefined): {
  state: TenantRolloutState;
  source: 'brand_config' | 'implied';
  rowVersion: 0;
  updatedAt: null;
} {
  const authored = parseLegacyTenantRolloutState(brandConfig?.rolloutState);
  if (authored) {
    return { state: authored, source: 'brand_config', rowVersion: 0, updatedAt: null };
  }
  return {
    state: defaultTenantRolloutState(),
    source: 'implied',
    rowVersion: 0,
    updatedAt: null,
  };
}

export function parseTenantRolloutStatePatch(value: unknown): TenantRolloutStatePatch | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 0 || keys.some((key) => !PATCH_KEYS.has(key))) return null;

  const candidate = parseTenantRolloutState({
    ...defaultTenantRolloutState(),
    ...record,
  });
  if (!candidate) return null;

  const patch: TenantRolloutStatePatch = {};
  if ('containedUntilUat' in record) patch.containedUntilUat = candidate.containedUntilUat;
  if ('releasedDispatchReliabilityFlagKeys' in record) {
    patch.releasedDispatchReliabilityFlagKeys = candidate.releasedDispatchReliabilityFlagKeys;
  }
  if ('sourceOperator' in record) patch.sourceOperator = candidate.sourceOperator;
  if ('rosterOperationalPack' in record) {
    patch.rosterOperationalPack = candidate.rosterOperationalPack;
  }
  return patch;
}

export function diffTenantRolloutState(
  before: TenantRolloutState,
  after: TenantRolloutState,
): TenantRolloutStatePatch {
  const patch: TenantRolloutStatePatch = {};
  if (before.containedUntilUat !== after.containedUntilUat) {
    patch.containedUntilUat = after.containedUntilUat;
  }
  const beforeReleased = [...before.releasedDispatchReliabilityFlagKeys].sort();
  const afterReleased = [...after.releasedDispatchReliabilityFlagKeys].sort();
  if (beforeReleased.join('\u0000') !== afterReleased.join('\u0000')) {
    patch.releasedDispatchReliabilityFlagKeys = afterReleased;
  }
  if (before.sourceOperator !== after.sourceOperator) {
    patch.sourceOperator = after.sourceOperator;
  }
  if (before.rosterOperationalPack !== after.rosterOperationalPack) {
    patch.rosterOperationalPack = after.rosterOperationalPack;
  }
  return patch;
}

export function isRolloutProtectionRelaxation(
  before: TenantRolloutState,
  after: TenantRolloutState,
): boolean {
  const beforeReleased = new Set(before.releasedDispatchReliabilityFlagKeys);
  return (
    (before.containedUntilUat && !after.containedUntilUat) ||
    after.releasedDispatchReliabilityFlagKeys.some((key) => !beforeReleased.has(key)) ||
    (before.sourceOperator !== 'AMANDA_R331' && after.sourceOperator === 'AMANDA_R331') ||
    (before.rosterOperationalPack !== 'MEDEA_V1' && after.rosterOperationalPack === 'MEDEA_V1')
  );
}

export const AMANDA_SOURCE_OPERATOR_DISABLED_MESSAGE =
  'This tenant is not opted into the Amanda source operator. Set rollout state sourceOperator to AMANDA_R331.';

export function isAmandaSourceOperatorEnabled(
  state: Pick<TenantRolloutState, 'sourceOperator'> | null | undefined,
): boolean {
  return state?.sourceOperator === 'AMANDA_R331';
}

export async function requireAmandaSourceOperator(
  sb: { from: (table: string) => unknown },
  tenantId: string | null | undefined,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { state } = await loadTenantRolloutState(sb, tenantId);
  if (!isAmandaSourceOperatorEnabled(state)) {
    return { ok: false, error: AMANDA_SOURCE_OPERATOR_DISABLED_MESSAGE };
  }
  return { ok: true };
}

type RolloutStateClient = {
  rpc?: (
    functionName: string,
    args: Record<string, unknown>,
  ) => PromiseLike<{
    data: unknown;
    error: { message: string } | null;
  }>;
  from: (table: string) => {
    select: (columns: string) => {
      eq: (
        column: string,
        value: string,
      ) => {
        maybeSingle: () => PromiseLike<{
          data: { brand_config: Record<string, unknown> | null } | null;
          error: { message: string } | null;
        }>;
      };
    };
  };
};

export async function loadTenantRolloutState(
  sb: { from: (table: string) => unknown },
  tenantId: string | null | undefined,
  options: { strict?: boolean } = {},
): Promise<TenantRolloutStateSnapshot> {
  if (!tenantId) {
    return {
      state: defaultTenantRolloutState(),
      source: 'implied',
      rowVersion: 0,
      updatedAt: null,
    };
  }
  const client = sb as RolloutStateClient;

  if (typeof client.rpc === 'function') {
    const { data: typedData, error: typedError } = await client.rpc(
      'get_tenant_rollout_state_controlled',
      { p_tenant_id: tenantId },
    );
    if (!typedError && typedData && typeof typedData === 'object' && !Array.isArray(typedData)) {
      const record = typedData as Record<string, unknown>;
      const state = parseTenantRolloutState(record.state);
      const rowVersion = Number(record.rowVersion);
      if (
        state &&
        record.source === 'typed' &&
        Number.isSafeInteger(rowVersion) &&
        rowVersion >= 0
      ) {
        return {
          state,
          source: 'typed',
          rowVersion,
          updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : null,
        };
      }
    }
  }

  const { data, error } = await client
    .from('tenants')
    .select('brand_config')
    .eq('id', tenantId)
    .maybeSingle();
  if (error) {
    if (options.strict) {
      throw new Error(`Could not load rollout state: ${error.message}`);
    }
    return {
      state: defaultTenantRolloutState(),
      source: 'implied',
      rowVersion: 0,
      updatedAt: null,
    };
  }
  if (!data && options.strict) {
    throw new Error('Could not load rollout state: tenant configuration was not found');
  }
  return readTenantRolloutState(data?.brand_config ?? {});
}
